import { useEffect, useRef, useCallback } from 'react';

/**
 * Optimized polling hook that:
 * 1. Only polls when tab is visible
 * 2. Uses configurable interval
 * 3. Cleans up properly
 * 4. Shares data across tabs via localStorage
 */
export const useOptimizedPolling = (
  callback,
  interval = 30000, // Default 30 seconds
  options = {}
) => {
  const {
    enabled = true,
    storageKey = null,
    immediate = true
  } = options;

  const savedCallback = useRef(callback);
  const intervalRef = useRef(null);
  const isPolling = useRef(false);

  // Update callback ref
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  const startPolling = useCallback(() => {
    if (!enabled || isPolling.current) return;

    isPolling.current = true;

    const poll = async () => {
      // Only poll if tab is visible
      if (document.hidden) {
        console.log('⏸️ Polling paused - tab not visible');
        return;
      }

      try {
        const result = await savedCallback.current();
        
        // Share data across tabs via localStorage if storageKey provided
        if (storageKey && result) {
          localStorage.setItem(storageKey, JSON.stringify({
            data: result,
            timestamp: Date.now()
          }));
          
          // Trigger storage event for other tabs
          window.dispatchEvent(new Event('storage'));
        }
      } catch (error) {
        console.error('Polling error:', error);
      }
    };

    // Run immediately if requested
    if (immediate) {
      poll();
    }

    // Set up interval
    intervalRef.current = setInterval(poll, interval);
  }, [enabled, interval, storageKey, immediate]);

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
      isPolling.current = false;
    }
  }, []);

  // Handle visibility change
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Tab hidden - stop polling silently
        stopPolling();
      } else {
        // Tab visible - resume polling silently
        startPolling();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [startPolling, stopPolling]);

  // Listen for data updates from other tabs
  useEffect(() => {
    if (!storageKey) return;

    const handleStorageChange = (e) => {
      if (e.key === storageKey || e.type === 'storage') {
        const stored = localStorage.getItem(storageKey);
        if (stored) {
          try {
            const { data, timestamp } = JSON.parse(stored);
            // Only use data if it's less than interval old
            if (Date.now() - timestamp < interval) {
              console.log('📡 Using shared data from another tab');
              // Data is already fresh from another tab, skip this poll cycle
            }
          } catch (error) {
            console.error('Error parsing shared data:', error);
          }
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [storageKey, interval]);

  // Start/stop polling based on enabled flag
  useEffect(() => {
    if (enabled && !document.hidden) {
      startPolling();
    } else {
      stopPolling();
    }

    return () => {
      stopPolling();
    };
  }, [enabled, startPolling, stopPolling]);

  return { startPolling, stopPolling };
};

export default useOptimizedPolling;
